import { useState } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { signOut } from '../services/cognito';
import EditNameModal from './EditNameModal';
import CacheStats from './CacheStats';
import Footer from './Footer';
import NotificationBell from './NotificationBell';

export default function Layout() {
  const { user, isAdmin, isSuperAdmin, clearAuth } = useAuthStore();
  const navigate = useNavigate();
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const handleSignOut = () => {
    signOut();
    clearAuth();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-white" style={{ height: 'auto', minHeight: '100vh' }}>
      {/* Header */}
      <header className="bg-white border-b border-gray-100 z-50 backdrop-blur-sm bg-white/95">
        <nav className="max-w-[3200px] mx-auto px-3 sm:px-4 lg:px-4 xl:px-6 2xl:px-8 h-14 sm:h-16 flex items-center justify-between">
          <div className="flex items-center space-x-3 sm:space-x-6 flex-1 min-w-0">
            {/* Hamburger Menu Button - Mobile Only */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="sm:hidden p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors flex-shrink-0"
              aria-label="Menu"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {showMobileMenu ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            <Link to="/feed" className="text-lg sm:text-2xl font-bold text-gray-900 hover:text-primary-600 transition-colors flex items-center gap-1 sm:gap-2 min-w-0 flex-shrink">
              {/* Phillips head screwdriver family pun */}
              <span className="text-sm sm:text-lg flex-shrink-0">🪛</span>
              <span className="truncate">Philips Family Album</span>
              <span className="text-sm sm:text-lg flex-shrink-0">🪛</span>
            </Link>
            
            {/* Desktop Menu */}
            <div className="hidden sm:flex space-x-1">
              <Link
                to="/feed"
                className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
              >
                Photos
              </Link>
              
              <Link
                to="/tags"
                className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
              >
                Tags
              </Link>
              
              {isSuperAdmin && (
              <Link
                  to="/statistics"
                className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
              >
                  Statistics
              </Link>
              )}
              
              {isAdmin && (
                <Link
                  to="/admin/users"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
                >
                  Admin
                </Link>
              )}
              
              <Link
                to="/tutorial"
                className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
              >
                Tutorial
              </Link>
              
                <Link
                to="/about"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
                >
                About
                </Link>
            </div>
          </div>

          <div className="flex items-center space-x-3 sm:space-x-4">
            {/* Notification Bell */}
            <NotificationBell />
            
            {/* Recycle Bin Icon (Super Admin Only) */}
            {isSuperAdmin && (
              <Link
                to="/recycle-bin"
                className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                title="Recycle Bin"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </Link>
            )}
            
            <div className="text-right hidden sm:block">
              <button
                onClick={() => setShowEditNameModal(true)}
                className="text-xs font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer block"
                title="Click to edit your name"
              >
                {user?.givenName} {user?.familyName}
              </button>
              <span className="text-xs text-gray-400 block">
                {user?.email}
              </span>
            </div>
            
            <button
              onClick={handleSignOut}
              className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
            >
              Sign Out
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Menu Dropdown */}
      {showMobileMenu && (
        <>
          {/* Backdrop */}
          <div 
            className="sm:hidden fixed inset-0 bg-black/20 z-40 backdrop-blur-sm"
            onClick={() => setShowMobileMenu(false)}
          />
          
          {/* Menu Panel */}
          <div className="sm:hidden fixed top-14 left-0 right-0 bg-white border-b border-gray-200 shadow-lg z-50 animate-slide-down">
            <nav className="px-4 py-3 space-y-1">
              <Link
                to="/feed"
                onClick={() => setShowMobileMenu(false)}
                className="block text-gray-700 hover:text-gray-900 hover:bg-gray-50 px-4 py-3 rounded-lg text-base font-medium transition-colors"
              >
                📸 Photos
              </Link>
              
              <Link
                to="/tags"
                onClick={() => setShowMobileMenu(false)}
                className="block text-gray-700 hover:text-gray-900 hover:bg-gray-50 px-4 py-3 rounded-lg text-base font-medium transition-colors"
              >
                🏷️ Tags
              </Link>
              
              {isSuperAdmin && (
              <Link
                  to="/statistics"
                onClick={() => setShowMobileMenu(false)}
                className="block text-gray-700 hover:text-gray-900 hover:bg-gray-50 px-4 py-3 rounded-lg text-base font-medium transition-colors"
              >
                  📊 Statistics
              </Link>
              )}
              
              {isAdmin && (
                <Link
                  to="/admin/users"
                  onClick={() => setShowMobileMenu(false)}
                  className="block text-gray-700 hover:text-gray-900 hover:bg-gray-50 px-4 py-3 rounded-lg text-base font-medium transition-colors"
                >
                  👤 Admin
                </Link>
              )}
              
                <Link
                to="/tutorial"
                  onClick={() => setShowMobileMenu(false)}
                  className="block text-gray-700 hover:text-gray-900 hover:bg-gray-50 px-4 py-3 rounded-lg text-base font-medium transition-colors"
                >
                📖 Tutorial
                </Link>
              
              <Link
                to="/about"
                onClick={() => setShowMobileMenu(false)}
                className="block text-gray-700 hover:text-gray-900 hover:bg-gray-50 px-4 py-3 rounded-lg text-base font-medium transition-colors"
              >
                ℹ️ About
              </Link>

              {/* User Info in Mobile Menu */}
              <div className="pt-3 mt-3 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowMobileMenu(false);
                    setShowEditNameModal(true);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  <div className="font-medium">{user?.givenName} {user?.familyName}</div>
                  <div className="text-xs text-gray-400">{user?.email}</div>
                  <div className="text-xs text-primary-500 mt-1">Tap to edit name</div>
                </button>
              </div>
            </nav>
          </div>
        </>
      )}

      {/* Main content */}
      <main className="max-w-[3200px] mx-auto px-2 sm:px-4 lg:px-4 xl:px-6 2xl:px-8 py-4 sm:py-6 lg:py-8">
        <Outlet />
      </main>

      {/* Footer */}
      <Footer />

      {/* Edit Name Modal */}
      {showEditNameModal && (
        <EditNameModal onClose={() => setShowEditNameModal(false)} />
      )}

      {/* Cache Statistics (floating button) */}
      <CacheStats />
    </div>
  );
}

